import { useMemo, useState } from 'react';
import { hotBoardData } from '@/mocks/homeData';

const tabs = [
  { key: 'all', label: '综合热度', icon: 'ri-fire-line' },
  { key: 'dispute', label: '争议最多', icon: 'ri-scales-3-line' },
  { key: 'rise', label: '上升最快', icon: 'ri-arrow-up-double-line' },
] as const;

type TabKey = (typeof tabs)[number]['key'];

export default function HotBoard() {
  const [active, setActive] = useState<TabKey>('all');

  const list = useMemo(() => {
    if (active === 'dispute') {
      return [...hotBoardData].sort((a, b) => {
        const scoreA = a.tag === '争议' ? 1 : 0;
        const scoreB = b.tag === '争议' ? 1 : 0;
        return scoreB - scoreA || b.heatPercent - a.heatPercent;
      });
    }
    if (active === 'rise') {
      return [...hotBoardData].sort((a, b) => {
        const scoreA = a.trend === 'up' ? 1 : 0;
        const scoreB = b.trend === 'up' ? 1 : 0;
        return scoreB - scoreA || b.heatPercent - a.heatPercent;
      });
    }
    return hotBoardData;
  }, [active]);

  const trendIcon = (trend: string) => {
    if (trend === 'up') return 'ri-arrow-up-line text-accent-600';
    if (trend === 'down') return 'ri-arrow-down-line text-primary-600';
    return 'ri-subtract-line text-foreground-500';
  };

  return (
    <section id="hot" className="w-full border-y border-background-200 bg-background-100/60">
      <div className="px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="flex items-center gap-2 font-label text-xs tracking-[0.2em] text-accent-600">
              <i className="ri-fire-line text-sm"></i>
              HOT RANKING
            </p>
            <h2 className="mt-3 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
              今日热帖榜
            </h2>
            <p className="mt-3 max-w-xl font-label text-sm leading-relaxed text-foreground-600">
              按热度、争议程度与上升速度三档排列，每小时刷新一次，方便你快速定位现在最值得围观的那几条。
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1 rounded-full border border-background-300/70 bg-background-50 p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 font-label text-xs transition-colors md:text-sm cursor-pointer ${
                  active === tab.key
                    ? 'bg-accent-500 text-background-50'
                    : 'text-foreground-700 hover:text-accent-700'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <ul className="mt-10 space-y-3">
          {list.map((item, index) => (
            <li
              key={item.id}
              className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-200 md:flex-row md:items-center md:gap-6 md:p-5"
            >
              <div className="flex items-center gap-4 md:w-16 md:shrink-0 md:flex-col md:gap-1">
                <span
                  className={`font-heading text-3xl font-black leading-none md:text-4xl ${
                    index === 0 ? 'text-accent-500' : index < 3 ? 'text-accent-400' : 'text-background-400'
                  }`}
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <i className={`${trendIcon(item.trend)} text-lg`}></i>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-label text-xs text-secondary-900">
                    {item.tag}
                  </span>
                  <span className="font-label text-xs text-foreground-500">{item.heat} 热度</span>
                </div>
                <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                  {item.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 font-label text-sm leading-relaxed text-foreground-600">
                  {item.summary}
                </p>
              </div>

              <div className="w-full md:w-44 md:shrink-0">
                <div className="flex items-center justify-between font-label text-xs text-foreground-500">
                  <span>热度条</span>
                  <span>{item.heatPercent}%</span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className={`h-full animate-grow rounded-full ${
                      index === 0 ? 'bg-accent-500' : 'bg-primary-400'
                    }`}
                    style={{ width: `${item.heatPercent}%` }}
                  ></div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}